import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "react-loading-skeleton/dist/skeleton.css";
import styles from './index.module.css';
import { downloadAllActivityLogs, getAllActivityLogs } from "../../../redux/features/activityLog/activityLogService";
import { clearActivityLogs, selectAllActivityLog, STATUS } from "../../../redux/features/activityLog/activityLogSlice";
import { getAllStaffMembers } from "../../../redux/features/staff/staffService";
import TimelineSkeleton from "../../../components/Skeleton/TimelineSkeleton";
import Timeline from "../../../components/Timeline";
import UpdatedDropdown from "../../../components/UpdatedDropdown";
import { faCircleXmark, faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation } from "react-router-dom";
import { showToast } from "../../../components/ToastMsg";
import NoResultsFound from "../../../components/NoResultsFound";

const eventStatus = [
    { id: '1', name: 'Bulk Upload Started', value: 'BulkUploadStarted' },
    { id: '2', name: 'Bulk Upload Failed', value: 'BulkUploadFailed' },
    { id: '3', name: 'Bulk Upload Partially Processed', value: 'BulkUploadPartiallyProcessed' },
    { id: '4', name: 'Bulk Upload Completed', value: 'BulkUploadCompleted' },
    { id: '5', name: 'Switch Entity', value: 'SwitchEntity' },
    { id: '6', name: 'Entity Create', value: 'EntityCreate' },
    { id: '7', name: 'Entity Update', value: 'EntityUpdate' },
    { id: '8', name: 'Course Create', value: 'CourseCreate' },
    { id: '9', name: 'Course Update', value: 'CourseUpdate' },
    { id: '10', name: 'Certificate Type Create', value: 'CertificateTypeCreate' },
    { id: '11', name: 'Certificate Type Update', value: 'CertificateTypeUpdate' },
    { id: '12', name: 'Certificate Template Create', value: 'CertificateTemplateCreate' },
    { id: '13', name: 'Certificate Template Update', value: 'CertificateTemplateUpdate' },
    { id: '14', name: 'Certificate Template Version Create', value: 'CertificateTemplateVersionCreate' },
    { id: '15', name: 'Certificate Template Version Update', value: 'CertificateTemplateVersionUpdate' },
    { id: '16', name: 'Certificate Pending', value: 'CertificatePending' },
    { id: '17', name: 'Certificate Verified', value: 'CertificateVerified' },
    { id: '18', name: 'Certificate Reprint With New Version', value: 'CertificateRePrintWithNewVersion' },
    { id: '19', name: 'Certificate Sent To Printing', value: 'CertificateSentToPrinting' },
    { id: '20', name: 'Certificate Printed', value: 'CertificatePrinted' },
    { id: '21', name: 'Certificate Issued', value: 'CertificateIssued' },
    { id: '22', name: 'Certificate Re Issued', value: 'CertificateReIssued' },
    { id: '23', name: 'Certificate Cancelled', value: 'CertificateCancelled' },
    { id: '24', name: 'Certificate Hold', value: 'CertificateHold' },
];

const limitSize = [
    { index: 0, name: '10', value: 10 },
    { index: 1, name: '20', value: 20 },
    { index: 2, name: '50', value: 50 },
    { index: 3, name: '100', value: 100 },
    { index: 4, name: 'Unlimited', value: 1 },
];

const ActivityLogs = () => {

    const dispatch = useDispatch();
    const location = useLocation();
    const eventDropdownRef = useRef(null);
    const userDropdownRef = useRef(null);

    const { staffMembers } = useSelector((state) => state.staff.allStaffMembers);

    const { data: activityLogs, status } = useSelector(selectAllActivityLog);

    const [selectedEvent, setSelectedEvent] = useState({
        id: '',
        name: ''
    });
    const [selectedEventValue, setSelectedEventValue] = useState(null);
    const [selectedUser, setSelectedUser] = useState({
        id: '',
        name: ''
    });
    const [isSelected, setSelected] = useState(false);

    const [pageLimit, setPageLimit] = useState(10);

    const fetchDetails = useCallback(() => {
        dispatch(getAllStaffMembers({}));
    }, [dispatch]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const fetchAllActivityLogs = useCallback(
        (filters = {}) => {
            const params = {
                audit_log_event: selectedEventValue,
                user_id: selectedUser.id,
                ...(pageLimit > 1 ? { limit: pageLimit } : {}),
                skip: 0,
                ...filters,
            };
            dispatch(getAllActivityLogs(params));
        },
        [dispatch, selectedEventValue, selectedUser, pageLimit]
    );


    useEffect(() => {
        if (isSelected) {
            fetchAllActivityLogs();
        }
    }, [fetchAllActivityLogs]);

    useEffect(() => {
        // Reset whenever path changes
        return () => {
            dispatch(clearActivityLogs());
            setSelectedEvent({ id: '', name: '' });
            setSelectedEventValue(null);
            setSelectedUser({ id: '', name: '' });
            setSelected(false);
            setPageLimit(10);
            if (eventDropdownRef.current) eventDropdownRef.current.clearSelection();
            if (userDropdownRef.current) userDropdownRef.current.clearSelection();
        };
    }, [location.pathname, dispatch]);

    const handleSelectEvent = async (option) => {
        setSelectedEvent({
            id: option.id,
            name: option.name
        });
        setSelectedEventValue(option.value);
        setSelected(true);
    };

    const handleUser = async (option) => {
        setSelectedUser({
            id: option.id,
            name: option.name
        });
        setSelected(true);
    };

    const handleLimit = async (option) => {
        setPageLimit(option);
    };

    const userOptions = staffMembers.map(user => ({
        id: user.id,
        name: user.full_name,
    }));

    const handleClearFilters = () => {
        if (eventDropdownRef.current) {
            eventDropdownRef.current.clearSelection();
        }
        if (userDropdownRef.current) {
            userDropdownRef.current.clearSelection();
        }
        setSelectedEvent({ id: '', name: '' });
        setSelectedEventValue(null);
        setSelectedUser({ id: '', name: '' });

        dispatch(clearActivityLogs());
        setSelected(false);
    };

    const downloadActivityLog = async () => {
        try {
            const params = {
                audit_log_event: selectedEventValue,
                user_id: selectedUser.id,
                username: selectedUser?.name,
            };
            await dispatch(downloadAllActivityLogs(params)).unwrap()
        } catch (error) {
            showToast('error', 'Failed to download the file.')
        }
    }
    
    return (
        <>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Activity Logs
                        {activityLogs?.data?.length > 0 &&
                            <FontAwesomeIcon icon={faDownload} onClick={downloadActivityLog} className={styles.downloadBtn}/>
                        }
                    </h1>
                </div>
                <div className={styles.searchSec}>
                    <UpdatedDropdown
                        ref={eventDropdownRef}
                        options={eventStatus}
                        placeholder="Select an Event"
                        onSelect={handleSelectEvent}
                        value={selectedEvent.name}
                    />
                    <UpdatedDropdown
                        ref={userDropdownRef}
                        options={userOptions}
                        placeholder="Select a User"
                        onSelect={handleUser}
                        value={selectedUser.name}
                    />
                    <div className={styles.clearButton} onClick={handleClearFilters}>
                        <FontAwesomeIcon icon={faCircleXmark} /> <span>Clear All</span>
                    </div>
                    {activityLogs?.data?.length > 0 &&
                        <div className={styles.btnGroup}>
                            {limitSize.map((limit) => (
                                <div key={limit.index} className={pageLimit === limit.value ? styles.activeBtn : styles.limitBtn} onClick={() => handleLimit(limit.value)}>{limit.name}</div>
                            ))}
                           </div>
                    }
                </div>
                <div className={`search-card-body ${styles.logsection}`}>
                    {(status === STATUS.LOADING) && (
                        <TimelineSkeleton />
                    )}
                    {status === STATUS.FAILED && (
                        <div className="error-text">
                            Failed to load activity logs
                        </div>
                    )}
                    {status === STATUS.SUCCEEDED && activityLogs.data.length === 0 && (
                         <NoResultsFound />
                    )}
                    {status === STATUS.SUCCEEDED && activityLogs.data.length > 0 && (
                        <Timeline data={activityLogs} />
                    )}
                </div>
            </div>
        </>
    );
};

export default ActivityLogs;
